import Button from "@material-ui/core/Button";
import {
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  ThemeProvider,
} from "@mui/material";
import TextFieldPassword from "components/input/TextFieldPassword";
import Modal from "components/modal/Modal";
import Select from "components/select/Select";
import { useFormik } from "formik";
import { useCityDistrictProvince } from "hooks/select/useCityDistrictProvince";
import { useMemo } from "react";
import muiTheme from "theme/muiTheme";
import {
  getCheckboxFieldProps,
  getSelectFieldProps,
  getTextFieldProps,
} from "ultis/form";

import {
  convertRoleCodeToListLevel,
  userValidationSchema,
} from "../utils/reportLicenseCSKH.util";

const DialogUpdateUser = ({
  item: propItem,
  listRole,
  open,
  onClose,
  onSubmit,
  loadingSaveUser,
}) => {
  const formik = useFormik({
    initialValues: {
      id: propItem?.id || "",
      username: propItem?.username || "",
      password: propItem?.password || "",
      role: propItem?.role || "",
      level: propItem?.level || "",
      city: propItem?.city || "",
      district: propItem?.district || "",
      postoffice: propItem?.postoffice || "",
      fullname: propItem?.fullname || "",
      email: propItem?.email || "",
      phoneNumber: propItem?.phoneNumber || "",
      status: propItem?.status || false,
      loginType: propItem?.loginType || "",
      phoneNumberVerified: propItem?.phoneNumberVerified || false,
      isActiveChat: propItem?.isActiveChat || false,
    },
    onSubmit,
    validationSchema: userValidationSchema,
  });

  const item = formik.values;

  const {
    listCityPostOfficeOptions,
    listDistrictPostOfficeOptions,
    listProvinceOptions,
  } = useCityDistrictProvince({
    citycode: item?.city,
    districtcode: item?.district,
  });

  const isEdit = !!item?.id;

  const listLevel = useMemo(
    () => convertRoleCodeToListLevel(item.role),
    [item.role]
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <Modal
        open={open}
        onClose={onClose}
        title={isEdit ? "Chỉnh sửa thông tin tài khoản" : "Thêm tài khoản"}>
        <form onSubmit={formik.handleSubmit}>
          <Stack width={550} spacing={2}>
            <TextField
              label="Tài khoản"
              {...getTextFieldProps(formik, "username")}
              autoFocus={true}
              disabled={isEdit}
            />

            <TextFieldPassword
              label="Mật khẩu"
              {...getTextFieldProps(formik, "password")}
            />

            <Select
              label="Phân quyền"
              options={listRole?.map((role) => {
                return { label: role.roleDisplayName, value: role.roleName };
              })}
              showSearch={false}
              {...getSelectFieldProps(formik, "role")}
              onChange={async (newValue) => {
                await formik.setFieldValue("role", newValue);
                await formik.setFieldValue("level", "");
                await formik.setFieldValue("city", "");
                await formik.setFieldValue("district", "");
                await formik.setFieldValue("postoffice", "");
              }}
            />

            <Select
              label="Cấp quản lý"
              showSearch={false}
              options={listLevel}
              {...getSelectFieldProps(formik, "level")}
              onChange={async (newValue) => {
                await formik.setFieldValue("level", newValue);
                await formik.setFieldValue("city", "");
                await formik.setFieldValue("district", "");
                await formik.setFieldValue("postoffice", "");
              }}
            />

            {(item?.level === "city" ||
              item?.level === "district" ||
              item?.level === "postoffice") && (
              <Select
                label="Bưu điện tỉnh/thành phố"
                {...getSelectFieldProps(formik, "city")}
                onChange={async (newValue) => {
                  await formik.setFieldValue("city", newValue);
                  await formik.setFieldValue("district", "");
                  await formik.setFieldValue("postoffice", "");
                }}
                options={listCityPostOfficeOptions}
              />
            )}

            {(item?.level === "district" || item?.level === "postoffice") && (
              <Select
                label="Bưu điện huyện"
                {...getSelectFieldProps(formik, "district")}
                onChange={async (newValue) => {
                  await formik.setFieldValue("district", newValue);
                  await formik.setFieldValue("postoffice", "");
                }}
                options={listDistrictPostOfficeOptions}
              />
            )}

            {item?.level === "postoffice" && (
              <Select
                label="Bưu cục"
                {...getSelectFieldProps(formik, "postoffice")}
                options={listProvinceOptions}
              />
            )}

            <TextField
              label="Họ tên"
              {...getTextFieldProps(formik, "fullname")}
            />

            <TextField label="Email" {...getTextFieldProps(formik, "email")} />

            <TextField
              label="Số điện thoại"
              {...getTextFieldProps(formik, "phoneNumber")}
              onChange={(event) => {
                if (!event.target.value)
                  formik.setFieldValue("isActiveChat", false);
                formik.setFieldValue("phoneNumber", event.target.value);
              }}
              disabled={isEdit ? item?.isActiveChat : false}
            />

            <Select
              label="Loại đăng nhập"
              {...getSelectFieldProps(formik, "loginType")}
              showSearch={false}
              options={[
                { label: "1", value: 1 },
                { label: "2", value: 2 },
              ]}
            />

            <Stack direction="row">
              <FormControlLabel
                control={
                  <Checkbox {...getCheckboxFieldProps(formik, "status")} />
                }
                label="Hoạt động"
              />
              <FormControlLabel
                sx={{ whiteSpace: "nowrap" }}
                control={
                  <Checkbox
                    {...getCheckboxFieldProps(formik, "phoneNumberVerified")}
                  />
                }
                label="Đã xác nhận số điện thoại"
              />
              {isEdit && (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...getCheckboxFieldProps(formik, "isActiveChat")}
                      disabled={!item.phoneNumber}
                    />
                  }
                  label="Kích hoạt chat"
                />
              )}
            </Stack>
          </Stack>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={onClose} color="secondary">
              Hủy
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loadingSaveUser}>
              Lưu
            </Button>
          </Stack>
        </form>
      </Modal>
    </ThemeProvider>
  );
};

export default DialogUpdateUser;
